/**
 * Typed testid registry for the `operations` domain — the SINGLE source of truth
 * for every `data-testid` this domain renders (TV1-2055, rolling out the
 * TV1-1866 pattern to phase-2 domains).
 *
 * Components spread these values into `data-testid`; the LIVE Playwright specs
 * (`src/tests/e2e/operations/reprice.spec.ts`, `bulk-publish.spec.ts`, and their
 * `helpers.ts`) import the SAME object for their selectors. A renamed key changes
 * the string in ONE place and both sides move together — instead of a runtime
 * "element not found" when a component testid drifts from the string a spec
 * hardcoded (e2e is not run in CI, so that drift fails silently). The
 * `teselly/no-raw-testid` ESLint rule forbids raw `data-testid` literals in this
 * domain so new testids must land here.
 *
 * Naming convention: `operations-[section]-[element]` per
 * `.claude/rules/e2e-testing.md`, BUT values are preserved VERBATIM from the
 * pre-existing literals (no rename) so the migration is value-safe — this domain
 * historically prefixed its testids `op-` rather than `operations-`, so the
 * values below keep that `op-` prefix. Per-run ids are built by the factory
 * functions below.
 *
 * NOTE: the diff-grid testids the review specs also touch (`op-diff-grid`,
 * `op-diff-row-<id>`, `op-approve-all`) belong to the reusable
 * `@components/application/SpreadsheetEditor/DiffReviewGrid` — NOT this domain —
 * so they are intentionally absent here and the specs reference them as raw
 * literals.
 */
export const operationsTestIds = {
  // --- run list (history) ---
  /** Operation-run history list container. */
  runList: 'op-run-list',
  /** A run row in the history table (keyed by run id). */
  runListRow: ( runId: string ): string => `op-run-list-row-${ runId }`,
  /** Status badge cell inside a history row (keyed by run id). */
  runStatusRow: ( runId: string ): string => `op-run-status-${ runId }`,
  /** Apply-progress cell inside a history row (keyed by run id). */
  runProgressRow: ( runId: string ): string => `op-run-progress-${ runId }`,

  // --- review surface ---
  /** Review surface container. */
  review: 'op-review',
  /** Operation-type badge in the review header. */
  reviewType: 'op-review-type',
  /** Risk-class badge in the review header. */
  reviewRisk: 'op-review-risk',
  /** Run-status badge in the review header (static — the per-row variant is `runStatusRow`). */
  runStatusBadge: 'op-run-status',
  /** "Proposed by the assistant" provenance badge — rendered only when proposedVia === 'assistant'. */
  reviewProposedByAssistant: 'op-review-proposed-by-assistant',
  /** Apply-progress block on the review surface (static — the per-row variant is `runProgressRow`). */
  runProgress: 'op-run-progress',
  /** External-irreversible confirmation gate container. */
  reviewRiskGate: 'op-review-risk-gate',
  /** "Entiendo que esto es irreversible" confirmation toggle. */
  reviewConfirmIrreversible: 'op-review-confirm-irreversible',
  /** "Rechazar" (reject) action button. */
  reject: 'op-reject',
  /** "Aprobar y aplicar" (apply) action button. */
  apply: 'op-apply',

  // --- scaffold-publications bespoke review renderer (TV1-2145) ---
  /** Summary-by-gap-class view (the DEFAULT view for a scaffold run). */
  scaffoldSummary: 'op-scaffold-summary',
  /** A gap-class row in the summary table (keyed by gap class, plus `ready` / `skipped`). */
  scaffoldSummaryRow: ( gapClass: string ): string => `op-scaffold-summary-row-${ gapClass }`,
  /** Approve-all checkbox on the scaffold summary view. */
  scaffoldApproveAll: 'op-scaffold-approve-all',
  /** "Approving creates drafts only" copy — the no-egress reassurance. */
  scaffoldDraftsNotice: 'op-scaffold-drafts-notice',
  /** Bounded per-gap-class drill-down view. */
  scaffoldDrilldown: 'op-scaffold-drilldown',
  /** "Volver al resumen" back action inside a drill-down. */
  scaffoldBack: 'op-scaffold-back'
} as const;
